/**
 * stripe-connect-ui.ts - Stripe Connect UI for tournament organizers
 *
 * Handles Stripe Connect onboarding and status display for accepting registration payments
 */

import { escapeHtml } from '../utils/security';
import { t } from '../utils/i18n-helper';
import { API_BASE_URL } from '../utils/constants';

interface TournamentRequirements {
  isPublic: boolean;
  allowsSelfRegistration: boolean;
}

interface ConnectStatus {
  connected: boolean;
  accountId?: string;
  chargesEnabled: boolean;
  payoutsEnabled: boolean;
  detailsSubmitted: boolean;
  error?: string;
}

interface TournamentPaymentStatus {
  paymentEnabled: boolean;
  accountConnected: boolean;
  accountId?: string;
  chargesEnabled?: boolean;
  payoutsEnabled?: boolean;
  detailsSubmitted?: boolean;
  onboardingRequired?: boolean;
  error?: string;
}

/**
 * Fetch user's Stripe Connect status
 */
export async function getConnectStatus(): Promise<ConnectStatus> {
  const response = await fetch('/api/connect/status', {
    credentials: 'include'
  });

  if (!response.ok) {
    throw new Error('Failed to fetch connect status');
  }

  return response.json();
}

/**
 * Start Stripe Connect onboarding
 */
export async function startOnboarding(): Promise<string> {
  const response = await fetch('/api/connect/onboard', {
    method: 'POST',
    credentials: 'include'
  });

  if (!response.ok) {
    const data = await response.json();
    throw new Error(data.error || 'Failed to start onboarding');
  }

  const data = await response.json();
  return data.url;
}

/**
 * Get Stripe Express dashboard link
 */
export async function getDashboardLink(): Promise<string> {
  const response = await fetch('/api/connect/dashboard', {
    method: 'POST',
    credentials: 'include'
  });

  if (!response.ok) {
    const data = await response.json();
    throw new Error(data.error || 'Failed to get dashboard link');
  }

  const data = await response.json();
  return data.url;
}

/**
 * Disconnect Stripe account from user profile (allows rewiring to a different account)
 */
export async function disconnectStripeAccount(): Promise<{ disconnectedFromTournaments: number }> {
  const response = await fetch('/api/connect/disconnect', {
    method: 'DELETE',
    credentials: 'include'
  });

  if (!response.ok) {
    const data = await response.json();
    throw new Error(data.error || 'Failed to disconnect Stripe account');
  }

  return response.json();
}

/**
 * Connect Stripe account to tournament
 */
export async function connectToTournament(tournamentId: string): Promise<void> {
  const response = await fetch(`/api/connect/tournaments/${tournamentId}/connect`, {
    method: 'POST',
    credentials: 'include'
  });

  if (!response.ok) {
    const data = await response.json();
    throw new Error(data.error || 'Failed to connect to tournament');
  }
}

/**
 * Disconnect Stripe account from tournament
 */
export async function disconnectFromTournament(tournamentId: string): Promise<void> {
  const response = await fetch(`/api/connect/tournaments/${tournamentId}/connect`, {
    method: 'DELETE',
    credentials: 'include'
  });

  if (!response.ok) {
    const data = await response.json();
    throw new Error(data.error || 'Failed to disconnect from tournament');
  }
}

/**
 * Get tournament payment status
 */
export async function getTournamentPaymentStatus(tournamentId: string): Promise<TournamentPaymentStatus> {
  const response = await fetch(`/api/connect/tournaments/${tournamentId}/status`, {
    credentials: 'include'
  });

  if (!response.ok) {
    throw new Error('Failed to fetch payment status');
  }

  return response.json();
}

/**
 * Render Stripe Connect status section in tournament settings
 * Simplified: The toggle in card.ts controls enable/disable - this just shows status
 */
export function renderConnectStatusSection(
  tournamentId: string,
  status: ConnectStatus,
  tournamentStatus: TournamentPaymentStatus,
  requirements?: TournamentRequirements
): string {
  const isConnectedToTournament = tournamentStatus.accountConnected;

  let statusHtml = '';
  let actionHtml = '';

  if (!status.connected) {
    // Not connected at all
    statusHtml = `
      <div class="connect-status connect-status-warning">
        <span class="status-icon">&#9888;</span>
        <span>${t('stripeConnect.notConnected', 'Stripe account not connected. Connect your account to accept registration payments.')}</span>
      </div>
    `;
    actionHtml = `
      <button type="button" class="btn btn-primary" onclick="startStripeOnboarding()">
        ${t('stripeConnect.connectAccount', 'Connect Stripe Account')}
      </button>
    `;
  } else if (!status.chargesEnabled) {
    // Connected but onboarding incomplete
    statusHtml = `
      <div class="connect-status connect-status-warning">
        <span class="status-icon">&#9888;</span>
        <span>${t('stripeConnect.onboardingIncomplete', 'Stripe onboarding incomplete. Complete setup to accept payments.')}</span>
      </div>
    `;
    actionHtml = `
      <button type="button" class="btn btn-primary" onclick="startStripeOnboarding()">
        ${t('stripeConnect.completeSetup', 'Complete Stripe Setup')}
      </button>
    `;
  } else {
    // Fully connected and ready
    if (isConnectedToTournament) {
      statusHtml = `
        <div class="connect-status connect-status-success">
          <span class="status-icon">&#10004;</span>
          <span>${t('stripeConnect.paymentsActive', 'Online payments are active for this tournament')}</span>
        </div>
      `;
    } else {
      statusHtml = `
        <div class="connect-status connect-status-success">
          <span class="status-icon">&#10004;</span>
          <span>${t('stripeConnect.ready', 'Stripe connected and ready to accept payments')}</span>
        </div>
      `;
    }
    actionHtml = `
      <div class="connect-action-buttons" style="display: flex; gap: 8px; flex-wrap: wrap;">
        <button type="button" class="btn btn-secondary btn-small" onclick="openStripeDashboard()">
          ${t('stripeConnect.dashboard', 'Stripe Dashboard')}
        </button>
        <button type="button" class="btn btn-neutral btn-small" onclick="changeStripeAccount('${tournamentId}')">
          ${t('stripeConnect.changeAccount', 'Change Account')}
        </button>
      </div>
    `;
  }

  return `
    <div class="stripe-connect-section">
      <p class="section-description">${t('stripeConnect.description', 'Accept registration payments via Stripe. A 2.5% platform fee applies.')}</p>

      ${statusHtml}

      <div class="connect-actions">
        ${actionHtml}
      </div>
    </div>
  `;
}

/**
 * Initialize Stripe Connect UI in fees section
 */
export async function initStripeConnectUI(tournamentId: string): Promise<void> {
  const container = document.getElementById(`stripe-connect-container-${tournamentId}`);
  if (!container) return;

  try {
    container.innerHTML = '<div class="loading">Loading payment settings...</div>';

    const [connectStatus, tournamentStatus, tournamentResponse] = await Promise.all([
      getConnectStatus(),
      getTournamentPaymentStatus(tournamentId),
      fetch(`${API_BASE_URL}/${tournamentId}`, { credentials: 'include' })
    ]);

    let requirements: TournamentRequirements | undefined;
    if (tournamentResponse.ok) {
      const tournament = await tournamentResponse.json();
      requirements = {
        isPublic: tournament.published === true,
        allowsSelfRegistration: tournament.allowSelfRegistration === true
      };
    }

    container.innerHTML = renderConnectStatusSection(tournamentId, connectStatus, tournamentStatus, requirements);
  } catch (error) {
    console.error('Failed to load Stripe Connect status:', error);
    container.innerHTML = `
      <div class="connect-status connect-status-error">
        <span class="status-icon">&#10060;</span>
        <span>Failed to load payment settings. Please refresh the page.</span>
      </div>
    `;
  }
}

/**
 * Handle start onboarding button click
 */
async function handleStartOnboarding(): Promise<void> {
  try {
    const url = await startOnboarding();
    window.location.href = url;
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Failed to start onboarding';
    alert('Error: ' + message);
  }
}

/**
 * Handle open dashboard button click
 */
async function handleOpenDashboard(): Promise<void> {
  try {
    const url = await getDashboardLink();
    window.open(url, '_blank');
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Failed to open dashboard';
    alert('Error: ' + message);
  }
}

/**
 * Handle connect to tournament button click
 */
async function handleConnectToTournament(tournamentId: string): Promise<void> {
  try {
    await connectToTournament(tournamentId);
    // Refresh the UI
    await initStripeConnectUI(tournamentId);
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Failed to enable payments';
    alert('Error: ' + message);
  }
}

/**
 * Handle disconnect from tournament button click
 */
async function handleDisconnectFromTournament(tournamentId: string): Promise<void> {
  if (!confirm('Are you sure you want to disable payments for this tournament? Existing registrations will not be affected.')) {
    return;
  }

  try {
    await disconnectFromTournament(tournamentId);
    // Refresh the UI
    await initStripeConnectUI(tournamentId);
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Failed to disable payments';
    alert('Error: ' + message);
  }
}

/**
 * Handle change Stripe account (disconnect and start new onboarding)
 */
async function handleChangeStripeAccount(tournamentId: string): Promise<void> {
  const confirmed = confirm(
    'This will disconnect your current Stripe account and allow you to connect a different one.\n\n' +
    'Note: Any tournaments using this account will have payments disabled until you connect a new account.\n\n' +
    'Do you want to continue?'
  );

  if (!confirmed) {
    return;
  }

  try {
    // Disconnect the current account
    await disconnectStripeAccount();

    // Start onboarding with a new account
    const url = await startOnboarding();
    window.location.href = url;
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Failed to change Stripe account';
    alert('Error: ' + message);
  }
}

// Export to window for onclick handlers
window.startStripeOnboarding = handleStartOnboarding;
window.openStripeDashboard = handleOpenDashboard;
window.connectStripeToTournament = handleConnectToTournament;
window.disconnectStripeFromTournament = handleDisconnectFromTournament;
window.changeStripeAccount = handleChangeStripeAccount;
window.initStripeConnectUI = initStripeConnectUI;

// Extend Window interface
declare global {
  interface Window {
    startStripeOnboarding: () => Promise<void>;
    openStripeDashboard: () => Promise<void>;
    connectStripeToTournament: (tournamentId: string) => Promise<void>;
    disconnectStripeFromTournament: (tournamentId: string) => Promise<void>;
    changeStripeAccount: (tournamentId: string) => Promise<void>;
    initStripeConnectUI: (tournamentId: string) => Promise<void>;
  }
}
